import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, ChevronLeft, Loader as LoaderIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Logo } from "@/components/layout/PublicLayout";

export default function ResetPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError(t("Die Passw\u00f6rter stimmen nicht \u00fcberein", "Passwords do not match"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      toast.success(t("Passwort ge\u00e4ndert. Bitte melden Sie sich an.", "Password changed. Please sign in."));
      navigate("/login");
    } catch (err) {
      setError(err.response?.data?.detail || t("Zur\u00fccksetzen fehlgeschlagen", "Reset failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen dark-section noise-overlay flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
        <div className="flex justify-center">
          <Logo dark />
        </div>
        <Card className="mt-8 rounded-[20px] border-white/10 bg-white/[0.06] backdrop-blur-md">
          <CardContent className="p-8">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-[12px] bg-[#FF3C20]/12 flex items-center justify-center shrink-0">
                <KeyRound className="h-5 w-5 text-[#FF3C20]" />
              </div>
              <div>
                <h1 className="font-display text-lg font-semibold text-white">{t("Neues Passwort setzen", "Set a new password")}</h1>
                <p className="text-xs text-white/50">{t("W\u00e4hlen Sie ein sicheres, neues Passwort f\u00fcr Ihr Konto.", "Choose a secure new password for your account.")}</p>
              </div>
            </div>
            {!token ? (
              <p className="mt-6 text-sm text-red-400" data-testid="reset-pw-no-token">
                {t("Ung\u00fcltiger Link: Es fehlt das Sicherheits-Token. Bitte nutzen Sie den Link aus Ihrer E-Mail oder fordern Sie einen neuen an.",
                   "Invalid link: security token missing. Please use the link from your email or request a new one.")}
              </p>
            ) : (
              <form onSubmit={submit} className="mt-7 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-white/80">{t("Neues Passwort", "New password")}</Label>
                  <Input type="password" required value={next} onChange={(e) => setNext(e.target.value)}
                    className="rounded-[10px] bg-white/5 border-white/15 text-white" data-testid="reset-pw-new-input" />
                  <p className="text-[11px] text-white/40">
                    {t("Min. 10 Zeichen, Gro\u00df- und Kleinbuchstaben sowie eine Zahl.", "Min. 10 characters, upper and lower case letters and a number.")}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-white/80">{t("Neues Passwort wiederholen", "Repeat new password")}</Label>
                  <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="rounded-[10px] bg-white/5 border-white/15 text-white" data-testid="reset-pw-confirm-input" />
                </div>
                {error && <p className="text-sm text-red-400" data-testid="reset-pw-error">{error}</p>}
                <Button type="submit" disabled={busy} className="w-full rounded-[12px] bg-[#FF3C20] text-white hover:bg-[#E8241B] active:scale-[0.98]" data-testid="reset-pw-submit">
                  {busy ? <LoaderIcon className="h-4 w-4 animate-spin" /> : t("Passwort speichern", "Save password")}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        <div className="mt-6 text-center">
          <Link to="/passwort-vergessen" className="inline-flex items-center gap-1 text-sm text-white/50 hover:text-white transition-colors" data-testid="reset-pw-request-new-link">
            <ChevronLeft className="h-4 w-4" /> {t("Neuen Link anfordern", "Request a new link")}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
